import React, { useState } from 'react';
import { AlertCircle, Vote, TrendingUp, FileText, MessageSquare, Users } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useWebSocket } from '../../context/WebSocketContext';
import Navbar from '../shared/Navbar';
import UncertaintiesTab from './UncertaintiesTab';
import VotingTab from './VotingTab';
import VisualizationsTab from './VisualizationsTab';
import DocumentsTab from './DocumentsTab';
import AssistantTab from './AssistantTab';

const SessionView = ({ onNavigateBack }) => {
  const { activeSession } = useAppContext();
  const { isConnected, participants } = useWebSocket();
  const [activeTab, setActiveTab] = useState('uncertainties');

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    window.location.reload();
  };

  const tabs = [
    { id: 'uncertainties', label: 'Uncertainties', icon: AlertCircle },
    { id: 'voting', label: 'Voting', icon: Vote },
    { id: 'visualizations', label: 'Analysis', icon: TrendingUp },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'assistant', label: 'AI Assistant', icon: MessageSquare }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar 
        title={activeSession?.name}
        subtitle={activeSession?.description}
        onLogout={handleLogout}
        showBack={true}
        onBack={onNavigateBack}
      />

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Connection status and participants */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-[#2e2ea8]' : 'bg-red-500'}`} />
              <span className="text-sm text-gray-600">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-gray-500" />
              <span className="text-sm text-gray-600">
                {participants.length} participant{participants.length !== 1 ? 's' : ''} online
              </span>
              {participants.length > 0 && (
                <div className="flex -space-x-2">
                  {participants.slice(0, 5).map((participant, idx) => (
                    <div
                      key={participant.user_id}
                      className="w-8 h-8 rounded-full bg-[#2e2ea8] flex items-center justify-center text-white text-xs font-semibold border-2 border-white"
                      title={participant.user_name}
                    >
                      {participant.user_name?.charAt(0).toUpperCase()}
                    </div>
                  ))}
                  {participants.length > 5 && (
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-xs font-semibold border-2 border-white">
                      +{participants.length - 5}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b border-gray-200">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-[#01006A] border-b-2 border-[#01006A]'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {activeTab === 'uncertainties' && <UncertaintiesTab />}
            {activeTab === 'voting' && <VotingTab />}
            {activeTab === 'visualizations' && <VisualizationsTab />}
            {activeTab === 'documents' && <DocumentsTab />}
            {activeTab === 'assistant' && <AssistantTab />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionView;